import { useCallback, useEffect, useState } from 'react';
import { AI_REVIEW_CHANGED_EVENT } from '../super-admin/NavBarAdmin';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import RefreshIcon from '@mui/icons-material/Refresh';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SearchIcon from '@mui/icons-material/Search';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import ClearIcon from '@mui/icons-material/Clear';
import { AiEmailDecisionRecord } from '../../../types';
import useAiApiRoutes, { SentEmail } from '../../hooks/api/useAiApiRoutes';
import { useContext } from 'react';
import UserContext from '../../context/user';

const BRAND = '#0088CF';
const BRAND_GRADIENT = 'linear-gradient(135deg, #0088CF, #00B7B2)';

// One neutral surface throughout; state is carried by a small colour accent
// and a word, never by washing a whole row in colour.
const SURFACE = '#ffffff';
const HAIRLINE = '#e6ebf1';
const INK = '#0f172a';
const INK_MUTED = '#64748b';

const STATUS_CHIP: Record<
  AiEmailDecisionRecord['status'],
  { label: string; color: string; background: string; accent: string }
> = {
  auto_applied: { label: 'Auto-applied', color: '#15803d', background: 'rgba(22,163,74,0.10)', accent: '#16a34a' },
  partially_applied: { label: 'Partially applied', color: '#b45309', background: 'rgba(217,119,6,0.10)', accent: '#d97706' },
  needs_review: { label: 'Needs review', color: '#b45309', background: 'rgba(217,119,6,0.12)', accent: '#f59e0b' },
  reviewed: { label: 'Reviewed', color: '#1d4ed8', background: 'rgba(59,130,246,0.10)', accent: '#3b82f6' },
  no_decisions: { label: 'No decisions', color: '#475569', background: 'rgba(100,116,139,0.10)', accent: '#cbd5e1' },
  error: { label: 'Error', color: '#b91c1c', background: 'rgba(220,38,38,0.10)', accent: '#dc2626' },
};

const FILTERS: { label: string; value: string }[] = [
  { label: 'All', value: '' },
  { label: 'Needs review', value: 'needs_review' },
  { label: 'Auto-applied', value: 'auto_applied' },
  { label: 'Reviewed', value: 'reviewed' },
  { label: 'No decisions', value: 'no_decisions' },
];

const CONFIDENCE_COLOR: Record<string, string> = {
  high: '#15803d',
  medium: '#b45309',
  low: '#b91c1c',
};

const ACTION_STYLE = {
  accept: { label: 'Accept', color: '#15803d', dot: '#16a34a' },
  decline: { label: 'Decline', color: '#b91c1c', dot: '#dc2626' },
  // The email named the container but the AI couldn't read its answer.
  unclear: { label: 'Unclear — needs your decision', color: '#b45309', dot: '#f59e0b' },
};

const formatTime = (value: string) => new Date(value).toLocaleString();

// 'unclear' while the admin hasn't chosen yet — it can't be submitted as-is.
export type DecisionEdit = { action: 'accept' | 'decline' | 'unclear'; reason: string };

const DecisionRow = ({
  decision,
  edit,
  onEditChange,
}: {
  decision: AiEmailDecisionRecord['decisions'][number];
  // Present only while the admin is correcting this record.
  edit?: DecisionEdit;
  onEditChange?: (next: DecisionEdit) => void;
}) => {
  const action = edit ? edit.action : decision.action;
  const style = ACTION_STYLE[action as keyof typeof ACTION_STYLE] || ACTION_STYLE.decline;
  const changed = !!edit && edit.action !== decision.action;

  return (
    <Box
      sx={{
        border: `1px solid ${changed ? '#fbbf24' : HAIRLINE}`,
        borderLeft: `3px solid ${changed ? '#f59e0b' : style.dot}`,
        borderRadius: 2,
        px: 1.75,
        py: 1.25,
        mb: 1,
        background: SURFACE,
      }}
    >
      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600, color: INK }}>
          {decision.containerCode || decision.bookingRef || '(unmatched)'}
        </Typography>
        {edit ? null : (
          <Typography variant="caption" sx={{ color: style.color, fontWeight: 700 }}>
            {style.label}
          </Typography>
        )}
        <Typography variant="caption" sx={{ color: CONFIDENCE_COLOR[decision.confidence] || INK_MUTED }}>
          {decision.confidence} confidence
        </Typography>
        {decision.adminOverride ? (
          <Typography variant="caption" sx={{ color: INK_MUTED }}>
            · set by admin
          </Typography>
        ) : null}
        {decision.changedPreviousOutcome ? (
          <Typography variant="caption" sx={{ color: '#b45309' }}>
            · changed an earlier outcome
          </Typography>
        ) : null}
        {decision.applied ? (
          <Typography variant="caption" sx={{ color: INK_MUTED }}>
            · applied{decision.appliedAt ? ` ${formatTime(decision.appliedAt)}` : ''}
          </Typography>
        ) : null}
        {decision.error ? (
          <Typography variant="caption" color="error">
            {decision.error}
          </Typography>
        ) : null}
      </Box>

      {edit && onEditChange ? (
        <Box mt={1} display="flex" alignItems="flex-start" gap={1.5} flexWrap="wrap">
          <ToggleButtonGroup
            exclusive
            size="small"
            value={edit.action === 'unclear' ? null : edit.action}
            onChange={(_, next) => next && onEditChange({ ...edit, action: next })}
            aria-label={`Outcome for ${decision.containerCode || 'this container'}`}
          >
            <ToggleButton value="accept" sx={{ textTransform: 'none', px: 1.5 }}>
              Accept
            </ToggleButton>
            <ToggleButton value="decline" sx={{ textTransform: 'none', px: 1.5 }}>
              Decline
            </ToggleButton>
          </ToggleButtonGroup>
          {edit.action === 'unclear' ? (
            <Typography variant="caption" sx={{ color: '#b45309', alignSelf: 'center' }}>
              Choose Accept or Decline
            </Typography>
          ) : null}
          {edit.action === 'decline' ? (
            <TextField
              size="small"
              label="Reason for the customer"
              value={edit.reason}
              onChange={(e) => onEditChange({ ...edit, reason: e.target.value })}
              sx={{ flex: '1 1 260px', minWidth: 220 }}
            />
          ) : null}
        </Box>
      ) : (
        <>
          {decision.reason ? (
            <Typography variant="caption" display="block" sx={{ color: INK_MUTED }}>
              Reason: {decision.reason}
            </Typography>
          ) : null}
          {decision.evidence ? (
            <Typography variant="caption" display="block" sx={{ color: INK_MUTED, fontStyle: 'italic' }}>
              “{decision.evidence}”
            </Typography>
          ) : null}
        </>
      )}
    </Box>
  );
};

// What each party actually received: the email that came in, and the emails
// the system sent about the same containers.
const EmailTrail = ({ record }: { record: AiEmailDecisionRecord }) => {
  const { getSentEmails } = useAiApiRoutes();
  const [sent, setSent] = useState<SentEmail[] | null>(null);
  const [shown, setShown] = useState<string | null>(null);

  useEffect(() => {
    getSentEmails(record._id).then((result) => setSent(result.emails));
  }, [getSentEmails, record._id]);

  const TEMPLATE_LABEL: Record<string, string> = {
    containerReuseRequest: 'Request to shipping line',
    reuseRequestOutcome: 'Outcome to customer',
  };

  const item = (key: string, title: string, meta: string, body: string, failed?: string) => (
    <Box key={key} sx={{ border: `1px solid ${HAIRLINE}`, borderRadius: 2, mb: 1, background: SURFACE }}>
      <Box
        px={1.75}
        py={1.25}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
        sx={{ cursor: 'pointer' }}
        onClick={() => setShown(shown === key ? null : key)}
      >
        <Box minWidth={0}>
          <Typography variant="body2" fontWeight={600} noWrap sx={{ color: INK }}>
            {title}
          </Typography>
          <Typography variant="caption" noWrap display="block" sx={{ color: INK_MUTED }}>
            {meta}
          </Typography>
          {failed ? (
            <Typography variant="caption" color="error" display="block">
              Not delivered: {failed}
            </Typography>
          ) : null}
        </Box>
        <Button size="small" sx={{ textTransform: 'none', flexShrink: 0 }}>
          {shown === key ? 'Hide' : 'View'}
        </Button>
      </Box>
      <Collapse in={shown === key}>
        <Divider />
        <Box
          px={1.75}
          py={1.5}
          sx={{
            maxHeight: 360,
            overflow: 'auto',
            fontSize: 13,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            color: INK,
          }}
        >
          {body || <span style={{ color: INK_MUTED }}>(no body recorded)</span>}
        </Box>
      </Collapse>
    </Box>
  );

  return (
    <Box mt={2}>
      <Typography variant="caption" sx={{ color: INK_MUTED, fontWeight: 700, letterSpacing: 0.4 }}>
        EMAILS
      </Typography>
      <Box mt={0.75}>
        {item(
          'inbound',
          'Received from shipping line',
          `${record.from || 'unknown sender'} · ${formatTime(record.receivedAt)}`,
          record.bodyText || ''
        )}
        {sent === null ? (
          <Typography variant="caption" sx={{ color: INK_MUTED }}>
            Loading sent emails…
          </Typography>
        ) : sent.length === 0 ? (
          <Typography variant="caption" sx={{ color: INK_MUTED }}>
            No sent emails recorded for these containers.
          </Typography>
        ) : (
          sent.map((email) =>
            item(
              email._id,
              TEMPLATE_LABEL[email.template] || email.template,
              `${email.subject || '(no subject)'} · to ${email.to || 'unknown'} · ${formatTime(email.sentAt)}`,
              // The text part reads better than raw HTML for a quick check.
              email.text || email.html || '',
              email.delivered ? undefined : email.error || 'send failed'
            )
          )
        )}
      </Box>
    </Box>
  );
};

const RecordCard = ({
  record,
  onApply,
  onMarkReviewed,
  applying,
  defaultOpen,
}: {
  record: AiEmailDecisionRecord;
  onApply: (id: string, overrides: { index: number; action: 'accept' | 'decline'; reason?: string }[]) => void;
  onMarkReviewed: (id: string) => void;
  applying: boolean;
  defaultOpen?: boolean;
}) => {
  const [open, setOpen] = useState(defaultOpen || record.status === 'needs_review');
  // null = viewing; an array = the admin is correcting this record.
  const [edits, setEdits] = useState<DecisionEdit[] | null>(null);
  const chip = STATUS_CHIP[record.status];
  // Unclear answers can't be applied as they stand — only an admin's choice can.
  const hasUnclear = record.decisions.some((d) => !d.applied && d.container_id && d.action === 'unclear');
  const hasUnapplied = record.decisions.some((d) => !d.applied && d.container_id && d.action !== 'unclear');
  const canReview = record.decisions.some((d) => d.container_id);
  // Anything not fully auto-applied can be cleared by hand: the admin may have
  // actioned the request outside StackIQ, or nothing in it matched a request.
  const canMarkReviewed = record.status !== 'auto_applied' && record.status !== 'reviewed';
  // A needs_review record with nothing StackIQ can apply — without this hint
  // the row shows no way out.
  const nothingToApply = record.status === 'needs_review' && !hasUnapplied && !hasUnclear && !canReview;

  const startReview = () => {
    setOpen(true);
    setEdits(record.decisions.map((d) => ({ action: d.action || 'decline', reason: d.reason || '' })));
  };

  // Only what the admin actually changed is sent; anything still unapplied is
  // applied by the same submit, so one email covers the whole request.
  const changedOverrides = (edits || [])
    .map((edit, index) => ({ edit, index }))
    .filter(({ edit }) => edit.action !== 'unclear')
    .filter(({ edit, index }) => edit.action !== record.decisions[index]?.action || edit.reason !== (record.decisions[index]?.reason || ''))
    .map(({ edit, index }) => ({ index, action: edit.action as 'accept' | 'decline', reason: edit.reason || undefined }));
  const undecidedCount = (edits || []).filter((e) => e.action === 'unclear').length;

  const submitReview = () => {
    onApply(record._id, changedOverrides);
    setEdits(null);
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 3,
        mb: 1.5,
        overflow: 'hidden',
        background: SURFACE,
        borderColor: HAIRLINE,
        // State reads from a single accent edge rather than a tinted card.
        borderLeft: `3px solid ${chip.accent}`,
        transition: 'box-shadow 150ms ease',
        '&:hover': { boxShadow: '0 4px 14px rgba(16,42,67,0.07)' },
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        px={2.25}
        py={1.5}
        sx={{ cursor: 'pointer' }}
        onClick={() => setOpen(!open)}
      >
        <Box minWidth={0} pr={1}>
          <Typography variant="body2" fontWeight={600} noWrap>
            {record.subject || '(no subject)'}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap display="block">
            {record.from} · {formatTime(record.receivedAt)} · {record.source}
            {record.summary ? ` — ${record.summary}` : ''}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={1} whiteSpace="nowrap">
          {record.decisions.length > 0 && !record.senderTrusted ? (
            <Chip
              size="small"
              label="Unverified sender"
              title="This email did not come from the shipping line's configured address, so nothing was applied automatically."
              sx={{ height: 22, fontWeight: 600, color: '#92400e', background: 'rgba(251,191,36,0.18)' }}
            />
          ) : null}
          <Chip
            size="small"
            label={chip.label}
            sx={{ height: 22, fontWeight: 600, color: chip.color, background: chip.background }}
          />
          <IconButton size="small" aria-label={open ? 'Collapse' : 'Expand'}>
            {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </Box>
      </Box>
      <Collapse in={open}>
        <Divider />
        <Box px={2.25} py={1.75} sx={{ background: SURFACE }}>
          {record.repliedToKind === 'cancellation' || record.repliedToKind === 'closed_request' ? (
            <Typography variant="caption" display="block" mb={1} sx={{ color: INK_MUTED }}>
              {record.repliedToKind === 'cancellation'
                ? 'Reply to our cancellation notice'
                : 'Reply to our earlier (now closed) request'}
              {record.repliedToContainerCodes && record.repliedToContainerCodes.length > 0
                ? ` for ${record.repliedToContainerCodes.join(', ')}`
                : ''}
              {record.repliedToVia ? ` (matched via ${record.repliedToVia})` : ''} — only containers it names by code
              can be decided from it.
            </Typography>
          ) : record.repliedToContainerCodes && record.repliedToContainerCodes.length > 0 ? (
            <Typography variant="caption" display="block" mb={1} sx={{ color: INK_MUTED }}>
              Reply to our request for {record.repliedToContainerCodes.join(', ')}
              {record.repliedToVia ? ` (matched via ${record.repliedToVia})` : ''} — a blanket answer applies to these.
            </Typography>
          ) : null}
          {record.error ? (
            <Typography variant="caption" color="error" display="block" mb={1}>
              {record.error}
            </Typography>
          ) : null}
          {record.decisions.length === 0 ? (
            <Typography variant="caption" sx={{ color: INK_MUTED }}>
              The AI found no re-use decisions in this email.
            </Typography>
          ) : (
            record.decisions.map((decision, index) => (
              <DecisionRow
                key={index}
                decision={decision}
                edit={edits ? edits[index] : undefined}
                onEditChange={
                  edits
                    ? (next) => setEdits(edits.map((current, i) => (i === index ? next : current)))
                    : undefined
                }
              />
            ))
          )}

          {edits ? (
            <Box display="flex" alignItems="center" gap={1} mt={1.5} flexWrap="wrap">
              <Button
                size="small"
                variant="contained"
                disabled={applying}
                onClick={submitReview}
                sx={{ textTransform: 'none', background: BRAND_GRADIENT }}
              >
                {applying ? 'Submitting…' : 'Submit and email customer'}
              </Button>
              <Button size="small" onClick={() => setEdits(null)} sx={{ textTransform: 'none', color: INK_MUTED }}>
                Cancel
              </Button>
              <Typography variant="caption" sx={{ color: INK_MUTED }}>
                {undecidedCount > 0
                  ? `${undecidedCount} container${undecidedCount > 1 ? 's' : ''} still need${undecidedCount > 1 ? '' : 's'} an Accept/Decline.`
                  : changedOverrides.length > 0
                  ? `${changedOverrides.length} change${changedOverrides.length > 1 ? 's' : ''} — the customer gets one email covering this request.`
                  : 'No changes yet.'}
              </Typography>
            </Box>
          ) : (
            <Box display="flex" alignItems="center" gap={1} mt={1.5} flexWrap="wrap">
              {hasUnapplied ? (
                <Button
                  size="small"
                  variant="contained"
                  disabled={applying}
                  onClick={() => onApply(record._id, [])}
                  sx={{ textTransform: 'none', background: BRAND_GRADIENT }}
                >
                  Apply decisions
                </Button>
              ) : null}
              {canReview ? (
                <Button
                  size="small"
                  variant="outlined"
                  disabled={applying}
                  onClick={startReview}
                  sx={{ textTransform: 'none' }}
                >
                  {hasUnclear ? 'Decide unclear containers' : hasUnapplied ? 'Change before applying' : 'Change decisions'}
                </Button>
              ) : null}
              {canMarkReviewed ? (
                <Tooltip title="Clears this email from the review queue without changing any containers or emailing anyone — for requests you have already actioned by hand.">
                  <Button
                    size="small"
                    // The only action on a row StackIQ can't apply anything
                    // for, so it takes the primary slot there.
                    variant={hasUnapplied ? 'outlined' : 'contained'}
                    startIcon={<TaskAltOutlinedIcon />}
                    disabled={applying}
                    onClick={() => onMarkReviewed(record._id)}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      ...(hasUnapplied
                        ? { color: BRAND, borderColor: BRAND, '&:hover': { borderColor: BRAND, background: 'rgba(0,136,207,0.06)' } }
                        : { background: BRAND_GRADIENT }),
                    }}
                  >
                    Mark as reviewed
                  </Button>
                </Tooltip>
              ) : null}
              {nothingToApply ? (
                <Typography variant="caption" sx={{ color: INK_MUTED }}>
                  None of these containers matched a pending request, so there is nothing StackIQ can apply.
                  Once it has been handled, mark it as reviewed.
                </Typography>
              ) : null}
              {record.status === 'reviewed' ? (
                <Typography variant="caption" sx={{ color: INK_MUTED }}>
                  Marked as reviewed{record.reviewedAt ? ` ${formatTime(record.reviewedAt)}` : ''} — handled outside
                  StackIQ; no containers were changed.
                </Typography>
              ) : null}
            </Box>
          )}
          {open ? <EmailTrail record={record} /> : null}
        </Box>
      </Collapse>
    </Paper>
  );
};

// Admin view of the AI shipping-line email pipeline: what came in, what the
// AI decided, what was auto-applied, and one-click apply for parked items.
// `statusFilter`, when provided, is controlled from outside (the Awaiting
// review stat card sets it to needs_review); the panel still owns its own
// state otherwise.
const AiEmailDecisionsPanel = ({
  onChanged,
  statusFilter: controlledStatus,
  onStatusFilterChange,
  focusRecordId,
  onClearFocus,
}: {
  onChanged?: () => void;
  statusFilter?: string;
  onStatusFilterChange?: (status: string) => void;
  // From the admin notice email's link: show just this record, expanded.
  focusRecordId?: string;
  onClearFocus?: () => void;
}) => {
  const { getEmailDecisions, processEmail, applyEmailDecisions, markEmailDecisionReviewed } = useAiApiRoutes();
  const {
    constants: { shippingLines },
  } = useContext(UserContext);
  const [records, setRecords] = useState<AiEmailDecisionRecord[] | null>(null);
  const [ownStatus, setOwnStatus] = useState('');
  const statusFilter = controlledStatus !== undefined ? controlledStatus : ownStatus;
  const setStatusFilter = (next: string) => {
    if (onStatusFilterChange) onStatusFilterChange(next);
    else setOwnStatus(next);
  };
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [lineFilter, setLineFilter] = useState('');
  const [applyingId, setApplyingId] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [pasteFrom, setPasteFrom] = useState('');
  const [pasteSubject, setPasteSubject] = useState('');
  const [processing, setProcessing] = useState(false);

  const load = useCallback(() => {
    if (focusRecordId) {
      getEmailDecisions(1, undefined, { id: focusRecordId }).then((result) => setRecords(result?.records ?? []));
      return;
    }
    getEmailDecisions(1, statusFilter || undefined, { search, shippingLineCode: lineFilter }).then((result) =>
      setRecords(result?.records ?? [])
    );
  }, [getEmailDecisions, statusFilter, search, lineFilter, focusRecordId]);

  useEffect(load, [load]);

  const refreshAll = () => {
    load();
    onChanged?.();
    window.dispatchEvent(new Event(AI_REVIEW_CHANGED_EVENT));
  };

  const apply = async (
    id: string,
    overrides: { index: number; action: 'accept' | 'decline'; reason?: string }[] = []
  ) => {
    setApplyingId(id);
    await applyEmailDecisions(id, overrides);
    setApplyingId(null);
    refreshAll();
  };

  const markReviewed = async (id: string) => {
    setApplyingId(id);
    await markEmailDecisionReviewed(id);
    setApplyingId(null);
    refreshAll();
  };

  const submitPaste = async () => {
    if (!pasteText.trim()) return;
    setProcessing(true);
    await processEmail({ from: pasteFrom, subject: pasteSubject, text: pasteText });
    setProcessing(false);
    setPasteText('');
    setDialogOpen(false);
    refreshAll();
  };

  return (
    <Box width="100%">
      <Box display="flex" alignItems="center" gap={1.5} mb={2} flexWrap="wrap">
        <TextField
          size="small"
          placeholder="Search container no., booking ref, sender…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          // Search on Enter rather than per keystroke: this scans decisions
          // across every stored email.
          onKeyDown={(e) => {
            if (e.key === 'Enter') setSearch(searchInput.trim());
          }}
          onBlur={() => setSearch(searchInput.trim())}
          sx={{ minWidth: 300, flex: '1 1 300px', background: '#fff', borderRadius: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: searchInput ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label="Clear search"
                  onClick={() => {
                    setSearchInput('');
                    setSearch('');
                  }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        <TextField
          select
          size="small"
          label="Shipping line"
          value={lineFilter}
          onChange={(e) => setLineFilter(e.target.value)}
          sx={{ minWidth: 180, background: '#fff', borderRadius: 1 }}
        >
          <MenuItem value="">All lines</MenuItem>
          {shippingLines.map((line) => (
            <MenuItem key={line.code} value={line.code}>
              {line.name}
            </MenuItem>
          ))}
        </TextField>
      </Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1.5} mb={2}>
        <Box display="flex" gap={1} flexWrap="wrap">
          {FILTERS.map((filter) => (
            <Chip
              key={filter.value}
              label={filter.label}
              clickable
              onClick={() => setStatusFilter(filter.value)}
              sx={
                statusFilter === filter.value
                  ? { background: 'rgba(0,136,207,0.12)', color: '#0f7ab5', fontWeight: 600 }
                  : { background: '#fff', border: '1px solid #e6ebf1' }
              }
            />
          ))}
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={refreshAll} aria-label="Refresh decisions">
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            onClick={() => setDialogOpen(true)}
            startIcon={<AutoAwesomeIcon sx={{ fontSize: 16 }} />}
            sx={{ textTransform: 'none', background: BRAND_GRADIENT, borderRadius: 2 }}
          >
            Process an email
          </Button>
        </Box>
      </Box>

      {records === null ? (
        <Box textAlign="center" mt={6}>
          <CircularProgress size={22} />
        </Box>
      ) : records.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{ borderRadius: 3, borderStyle: 'dashed', borderColor: '#dbe3ea', py: 7, textAlign: 'center' }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              background: 'rgba(0,136,207,0.10)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 1.5,
            }}
          >
            <MarkEmailReadOutlinedIcon sx={{ fontSize: 30, color: BRAND }} />
          </Box>
          <Typography fontWeight={600} mb={0.5}>
            {search
              ? `No emails matching "${search}"`
              : statusFilter || lineFilter
              ? 'Nothing matches these filters yet'
              : 'No shipping-line emails processed yet'}
          </Typography>
          <Typography variant="body2" color="text.secondary" maxWidth={440} mx="auto" mb={2}>
            When availability emails arrive (or are pasted in), the AI reads them against pending reuse
            requests and applies accept/decline decisions automatically.
          </Typography>
          <Button
            variant="outlined"
            onClick={() => setDialogOpen(true)}
            sx={{ textTransform: 'none', borderColor: BRAND, color: BRAND, borderRadius: 2 }}
          >
            Process an email
          </Button>
        </Paper>
      ) : (
        <>
          {focusRecordId ? (
            <Box display="flex" alignItems="center" gap={1} mb={1.5}>
              <Typography variant="body2" color="text.secondary">
                Showing the record from your notification email.
              </Typography>
              <Button size="small" onClick={onClearFocus} sx={{ textTransform: 'none' }}>
                Show all emails
              </Button>
            </Box>
          ) : null}
          {records.map((record) => (
            <RecordCard
              key={record._id}
              record={record}
              onApply={apply}
              onMarkReviewed={markReviewed}
              applying={applyingId === record._id}
              defaultOpen={record._id === focusRecordId}
            />
          ))}
        </>
      )}

      <Dialog open={dialogOpen} onClose={() => !processing && setDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontWeight: 700 }}>Process a shipping-line email</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Paste the email below — StackIQ will match it against pending reuse requests and record
            accept/decline decisions.
          </Typography>
          <Box display="flex" gap={1.5} mb={1.5}>
            <TextField size="small" label="From" value={pasteFrom} onChange={(e) => setPasteFrom(e.target.value)} sx={{ flex: 1 }} />
            <TextField size="small" label="Subject" value={pasteSubject} onChange={(e) => setPasteSubject(e.target.value)} sx={{ flex: 2 }} />
          </Box>
          <TextField
            fullWidth
            multiline
            minRows={8}
            size="small"
            label="Email body"
            placeholder="Paste the shipping line's reply here…"
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={processing} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={processing || !pasteText.trim()}
            onClick={submitPaste}
            startIcon={processing ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : undefined}
            sx={{ textTransform: 'none', background: BRAND_GRADIENT, borderRadius: 2 }}
          >
            {processing ? 'Processing…' : 'Process with AI'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AiEmailDecisionsPanel;
